import { isObject, mergeWith } from 'lodash';
import type { InteractionContact } from '@/common/types/interaction';
import type {
  AgentBrokerage,
  AgentContact,
  BaseContact,
  ClientContact,
  ContactsFormData,
  ContactType,
} from './types';

export type ContactRow = ContactsFormData['contacts'][ContactType][number];

type ContactOfType<T extends InteractionContact['type']> = Extract<
  InteractionContact,
  { type: T }
>;

const emptyBrokerage = (): AgentBrokerage => ({
  companyName: null,
  licenseNumber: null,
  mlsCode: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
});

const emptyBase = (): BaseContact => ({
  firstName: null,
  lastName: null,
  email: null,
  cellPhone: null,
  homePhone: null,
  companyName: null,
  googleContactId: null,
  followUpBossContactId: null,
  contactSource: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
  county: null,
});

const emptyAgent = (): AgentContact => ({
  ...emptyBase(),
  licenseNumber: null,
  mlsId: null,
  userId: null,
  brokerage: emptyBrokerage(),
});

const emptyClient = (): ClientContact => ({
  ...emptyBase(),
  nickname: null,
  entityType: 'individual',
  isSuccessor: null,
  trust: { trustName: null, title: null, titleOther: null },
  entity: { entityName: null, title: null, titleOther: null },
  probate: { estateName: null, title: null, titleOther: null },
  powerOfAttorney: { firstName: null, lastName: null, email: null },
});

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

export function fillBlankFields<T>(row: T, incoming: ContactRow): T {
  return mergeWith({}, incoming, row, (incomingValue, rowValue) => {
    if (isObject(incomingValue) || isObject(rowValue)) return undefined;
    return isBlank(rowValue) ? incomingValue : rowValue;
  });
}

// The four primary contacts (own-side + counterparty agent/client) are seeded
// with one empty row when absent so the form renders an editable card immediately.
// Every other role hydrates as the raw server rows grouped by `type` — no
// per-field mapping, since the form binds to the contact shape directly.
export function hydrateContacts(
  contacts: readonly InteractionContact[],
): ContactsFormData {
  const rows = <T extends InteractionContact['type']>(type: T) =>
    contacts
      .filter((c): c is ContactOfType<T> => c.type === type)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const agents = rows('agent');
  const clients = rows('client');
  const counterpartyAgents = rows('counterpartyAgent');
  const counterpartyClients = rows('counterpartyClient');

  return {
    contacts: {
      agent: agents.length ? agents : [emptyAgent()],
      client: clients.length ? clients : [emptyClient()],
      counterpartyAgent: counterpartyAgents.length
        ? counterpartyAgents
        : [emptyAgent()],
      counterpartyClient: counterpartyClients.length
        ? counterpartyClients
        : [emptyClient()],
      counterpartyTransactionCoordinator: rows(
        'counterpartyTransactionCoordinator',
      ),
      titleOfficer: rows('titleOfficer'),
      escrowOfficer: rows('escrowOfficer'),
      lender: rows('lender'),
      appraiser: rows('appraiser'),
      attorney: rows('attorney'),
      counterpartyAttorney: rows('counterpartyAttorney'),
      closingAttorney: rows('closingAttorney'),
      propertyManagingAgent: rows('propertyManagingAgent'),
      homeWarranty: rows('homeWarranty'),
      other: rows('other'),
    },
  };
}

export { emptyAgent, emptyBase, emptyClient };
